import { CurvePathData } from '@elfalem/leaflet-curve';
import { Circle, circle, latLng, Marker } from 'leaflet';
import { IconHelper } from 'src/app/core/leaflet-helper/icon-helper';
import { AssetStatus } from 'src/app/maps/domain/livedata/status.enum';
import { MapElement } from 'src/app/maps/domain/masterdata/map-element.model';
import { SisMapAssetCategory } from 'src/app/maps/domain/masterdata/sismap-asset-category.enum';
import { CurveEndMarkers } from 'src/app/maps/map/style/curve-end-markers.model';
import { IconColor } from 'src/app/maps/map/style/icon-color.model';
import { MapStyle } from 'src/app/maps/map/style/styles/map-style.base';

export class MapStyleAndermatt extends MapStyle {
  readonly name = 'andermatt';

  private readonly curveEndMarkerVisibleRadius = 2;
  private readonly curveEndMarkerHighlightRadius = 5;

  private readonly urlBlobPath = 'sismap/styles/andermatt/';
  private readonly statusIconNames = ['closed', 'disabled', 'preparation', 'open'];
  private readonly iconNameLetterMap = new Map<string, string>([
    ['zahnradbahn', 'a'],
    ['gondelbahn', 'd'],
    ['pendelbahn', 'f'],
    ['sesselbahn3', 'g'],
    ['sesselbahn4', 'g'],
    ['sesselbahn6', 'g'],
    ['skilift', 'l'],
    ['foerderband', '»'],
    ['pistefreeride', 'j'],
    ['pisteleicht', 'j'],
    ['pistemittel', 'j'],
    ['pisteschwer', 'j'],
    ['rennpiste', 'j'],
    ['schlitteln', 'h'],
    ['rodeln', '\u{201A}'],
    ['langlaufklassisch', '\u{203A}'],
    ['langlaufskating', '\u{201C}'],
    ['crosscountrycrossskillspark', '\u{201C}'],
    ['wandern', 'u'],
    ['schneeschuh', '\u{20BD}'],
    ['sos', 'n'],
    ['kinderskiland', '–'],
    ['funpark', '‹'],
    ['snowpark', '‹'],
    ['hotel', '/'],
    ['restaurant', '\u{0027}'],
    ['bar', '•'],
    ['lvs', '¡'],
    ['webcam', '\u{0F0009}'],
    ['atc', '\u{0F0000}'],
  ]);

  private readonly layerIconNameLetterMap = new Map<string, string>([
    ['anlagen', 'd'],
    ['pisten', 'j'],
    ['langlauf', '\u{203A}'],
    ['schlitteln', 'h'],
    ['wandern', 'u'],
    ['schneeschuh', '\u{20BD}'],
    ['bike', '('],
    ['gastro', '\u{0027}'],
    ['poi', '฿'],
    ['webcam', '\u{0F0009}'],
    ['winter', '˙'],
    ['summer', '˘'],
  ]);

  constructor(protected iconHelper: IconHelper) {
    super(iconHelper);
  }

  async getIconSvg(iconName: string): Promise<string> {
    if (this.statusIconNames.includes(iconName)) {
      return this.iconHelper.getIcon(`${this.urlBlobPath}${iconName}.svg`);
    }

    const letter = this.iconNameLetterMap.get(iconName);
    if (!letter) {
      return '';
    }

    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" viewbox="0 0 100 100">
        <circle cx="50%" cy="50%" r="40%" fill="#ffffff"></circle>
        <text x="50%" y="90%" text-anchor="middle" style="font-family:'ASD Piktogramme Medium-Negativ';font-size:91.43px;">${letter}</text>
        <circle id="circle" cx="50%" cy="50%" r="42%" fill="none" stroke="#989898" stroke-width="6px"></circle>
      </svg>`;

    return svg;
  }

  getLayerIconSvgUrl(layerIconName: string, alternateStyle: boolean): string {
    const letter = this.layerIconNameLetterMap.get(layerIconName);
    if (!letter) {
      return '';
    }

    const fontFamily = alternateStyle ? 'ASD Piktogramme Medium-Negativ' : 'ASD Piktogramme Medium-Positiv';
    // urls generated with https://transfonter.org/ (tick base64 and input the required characters): u02D8, u02D9, u0064, u006A, u0068, u0075, u0027, u0E3F, u0F0009, u20BD, 203A
    const fontSrc = alternateStyle
      ? `url('data:font/woff2;charset=utf-8;base64,d09GMgABAAAAAAhoAAwAAAAAEHAAAAgYAAEAAAAAAAAAAAAAAAAAAAAAAAAAAAAAGhYGYACCIBEICpJQj1sLIAABNgIkAygEIAWHFgdXGwcOo6KUcKaQ/dUBT0T6BTeERTgIFKWIWI7FM5tPrea0Ykr/E86fzysiHrVvWq8tKscwEIkFwzmSvMl/0vv+3Mwk773MRCtksxKq/TA7qhIrolyXH0DRUfc/t6FRXrfD5M/bEiJSNz/UQqJkGqURiXRChyibbPa+oqmtqajxJZjGIwxSYg3Gg0ZiseSG8LQbERUeqIJKJjws9v+kZ+d7194mr1WTEu3+IsBAEYzdrUwOSbKu0acAJeB8c7+6QzURvxxeilrolLLt631zVEZSH2qJTiSReCRTLZWmoZA5aleCZqs7/ywPIIAEOI6CMCbCRxx9oYIy0zsAVcqctIFPFihAowUn4424TTvUG173IAwXFKvDp9yFftUj9mIMDmDSJAbtxMXEOjvojtzs2DA+1qPGk6R9dO5Uz83Tq/B6Ic/s2W/RH/cs6ve6P/fp1oPLD9RsNLHETfcAIfVUwExTATedDlTT2UAznQt8pisYflBe2NsCARvztICw8a4WkDbe0wsMloAjYFLUFA7AAAAXa/DCdlD8IDwjmA5AziFCBzzepLjr0LBJcKnpmuTcjrVtkyJYQ5yqxx3scJkbrUmbazaSr8tu8EWktFOlz/ZrEdJvc31KmMdYjdGgWtUr2OGgBnE10QISpMopEoPjqgIS7VlIpXa26+lTip880d2jR4iuPRb7Fixh2KgdRkt1iqbqbh8Zd5I0XiP8GWwSeEOLJ0/cPX0q/OPHG7z6SN9WwOFsVdBoeLhlARkBRnTE0Fy1BGoSA5LoqeLa9bYCAg16uSc4fB7qbpw1zW93VbXP8sMklU5A6v2GVhGtpEZ8lrZUCGgpv0z4VB2RhX5tI1aIBU6G6URLDVNereYehpUV4Q+lsYbeBxF6L9SFcYW2jdbMjU32+oAKJmEzMSAJUPdOTRVVS3X7p29ITmW5gELlAuGK1A5XyofZQlJZuntCcwCNyEM7ZYjrIK1LkvQwDEmwCuHOLzXMRhrWjIWNOgLVdkQToqGGmFwPV4oA164D0mq9GqJqVWjBDveVrE/XCN8LEBT3C38mkh+D6KLuHrj4zClLb3qF76e4L4zegw+0y/f/OFftISLJ4Rdh+S5xXjuYEFJ3pObdC8Wd/JCaw3ki+v6y/hzFZ3V3GluiTbq57P9mgZZUAeXt5FvCPNb8qTUNcjZxh4ZpDQrEhBGgzmUugdrS/62lHuyhmQ/lpIiaLOBIKn58ovpPRfK7JWaMqRNkcmKWxwsFmTHLtixZvmTZ+SVEcSw5JotQx3Ratnl6DHdYAl4s5JY7cBO4edYlcIVEg8ONKeNrBAOHQ9QcF42JjwEfFjocCye5JNzFybphWq/UyBYdpRMTp0KzeSMQHgJehJ4N5dBLklo2N9oQjWtAQ3ooG2NBTjfjv7p4R9VVR94XPuBVxBrn95tlak+eM3vwvjQtpPVfv+puqNJBOv+Nq2HjeOYT3zoG5mQt/8DSD+1aO8UDzsa/Ejf8mmLlw+j2DYcNz9+YhOB5rIkzHu8qh3UXZRiTLgkbrck92J8IhYcbZaKNliZnLE1M10eBOYETOO/LuekqJYSQ6/WsnPjmb3Ftce1tSQzjgvLZjd1vRYVcz5z3M/hAo72gI+4n64+mwtGpJ6NzNPrmDa7df8htHOh4uN9O+9hIbmVaRJTuQLPCT1XaGEfEV49O75UsqMoY0BqvTG1I7SKauGTDVgeiGzcv5n08dzTTjKe+Wsx8mfZMnvXSLdP7xu40+fzIsdzo47Pk0RPrqjMzKQ+uMa6FdUbt+V+oSlfWVLfuaqaSX4YYXtBoYVqQlR3mieFmnCZK1nFb2ByuU5N1Yy0HEW+PxEUpOokkz+BiuAx5nFCnGmj48ky8AOezciVwn+p2fSRhLI9ypuLbG4d6gXshk2RHGn5g1DhqQLpqL4z91MqO1ll1Z9OThhSnFwxJ9bP2HXRZSu8vTLVx6+Q7Y9c8LY0QBNevYHD54oGVjkezx0RHRpEP1Qnm52qTWpdZBSFJjuvBiQS2eLSDvOF6gwJhaS8UNGzZ6sA9IagnDI108hJTJYmN84ZhXqlxYz+v2g2d4sCLpxljzX1e1YMC9Al/2zYPPHnQ8U1J76HxCSwftJXaCUT/UqeQSwWfLudGbtwLmbUKhoYFQRYEmhFsmxkp8M+/Xb/DfADYPScmOhZO7AE4hRPJz+lztd1HVIlWZby4k882xLZQhauqX2wyXQ06emGUZvykqMGvjfyCeaCAl687rxQFvF/8jUH9Lxsj85L8lBa8jSr0KMHm0m/SMNR8McEBtKiUe92BwetkK5aV33I9EoXLrZtO8xaRFNgkWhBC4vJChU82ZNKJrXk5VJkmQE2sBQyI8oCC49la4tDmdQ2XVvIYAkNTAIDri0NS5Q8VZjIg052y1pyEqphsh5qKnIQBRXkHhYQKaS1xaAtWyckc/hq+Cmp1GKrWRcc75Kv5YQl6uDhu/iBTov9oezXS0U35iOcnghbk2Jy8vMLK7WQkVa5L+UJ4Rwm5eOOrJdbqZATS3K3tcc2lvINA7lBlWEgOJULaoRsfDfVg1Iunpg4MqSwXOnQH8qryhkpAD1o42jDtyVY6pQNtm9KgUzyW6DCUSXaU488GeOzJEt/5HodFY7EEkrGmqQ4JvBGM/wmYUHsH2GLQGhAtBUjOB98IyrGoVArWsbqlLG5OUfzJoX/xRUd0bYTtfB7EdKzx87DxjNpOi/H/Vmlk1tGcQ9K1amqb+eEVFfrDiPtWtCFbFiruZDzbUHKvELJsXjkAAA==') format('woff2');`
      : `url('data:font/woff2;charset=utf-8;base64,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') format('woff2');`;

    const svg = `<svg xmlns="http://www.w3.org/2000/svg" height="100" width="100" viewbox="0 0 100 100">
      <style>
        @font-face {
          font-family: ${fontFamily};
          src: ${fontSrc}
        }
      </style>
      <circle cx="50%" cy="50%" r="46%" fill="#fff" stroke="#fff" stroke-width="3"/>
      <text x="50%" y="50%" text-anchor="middle" dy=".445em" style="font-family:${fontFamily}, 'ASD Piktogramme';font-weight:500;font-size:100px;">${letter}</text>
    </svg>`;

    const blob = new Blob([svg], { type: 'image/svg+xml' });
    const svgUrl = URL.createObjectURL(blob);

    return svgUrl;
  }

  getSeasonIconSvgUrl(layerIconName: string, alternateStyle: boolean): string {
    return this.getLayerIconSvgUrl(layerIconName, alternateStyle);
  }

  createCurveEndMarkers(curveCoords: CurvePathData, color: string, element: MapElement, pane: string): CurveEndMarkers {
    if (curveCoords.length && element.category === SisMapAssetCategory.Lift) {
      const start: number[] = curveCoords.find((v) => Array.isArray(v)) as number[];
      const end: number[] = curveCoords[curveCoords.length - 1] as number[];

      const latLngStart = latLng(start[0], start[1]);
      const latLngEnd = latLng(end[0], end[1]);

      return {
        visible: [
          circle(latLngStart, {
            radius: this.curveEndMarkerVisibleRadius,
            color,
            fillColor: color,
            fillOpacity: 1,
            pane,
          }),
          circle(latLngEnd, {
            radius: this.curveEndMarkerVisibleRadius,
            color,
            fillColor: color,
            fillOpacity: 1,
            pane,
          }),
        ],
        highlight: [
          circle(latLngStart, {
            radius: this.curveEndMarkerHighlightRadius,
            color,
            fillColor: color,
            fillOpacity: 0,
            opacity: 0,
            pane,
          }),
          circle(latLngEnd, {
            radius: this.curveEndMarkerHighlightRadius,
            color,
            fillColor: color,
            fillOpacity: 0,
            opacity: 0,
            pane,
          }),
        ],
      };
    }

    return { visible: [], highlight: [] };
  }

  updateCurveEndMarkers(
    curveCoords: CurvePathData,
    curveEndMarkers: CurveEndMarkers,
    color: string,
    element: MapElement,
  ): void {
    if (element.category === SisMapAssetCategory.Lift) {
      const start: number[] = curveCoords.find((v) => Array.isArray(v)) as number[];
      const end: number[] = curveCoords[curveCoords.length - 1] as number[];

      const latLngStart = latLng(start[0], start[1]);
      const latLngEnd = latLng(end[0], end[1]);

      const style = {
        color,
        fillColor: color,
      };

      (curveEndMarkers.visible[0] as Circle).setLatLng(latLngStart).setStyle(style);
      (curveEndMarkers.visible[1] as Circle).setLatLng(latLngEnd).setStyle(style);
      (curveEndMarkers.highlight[0] as Circle).setLatLng(latLngStart).setStyle(style);
      (curveEndMarkers.highlight[1] as Circle).setLatLng(latLngEnd).setStyle(style);
    }
  }

  zoomCurveEndMarkers(curveEndMarkers: CurveEndMarkers, scaling: number): void {
    curveEndMarkers.visible.forEach((marker) =>
      (marker as Circle).setRadius(this.curveEndMarkerVisibleRadius + 0.8 * scaling),
    );
    curveEndMarkers.highlight.forEach((marker) =>
      (marker as Circle).setRadius(this.curveEndMarkerHighlightRadius + 0.8 * scaling),
    );
  }

  setHighlight(curveEndMarkers: CurveEndMarkers, highlight: boolean): void {
    (curveEndMarkers.highlight[0] as Circle)?.setStyle({
      fillOpacity: highlight ? 0.5 : 0,
    });

    (curveEndMarkers.highlight[1] as Circle)?.setStyle({
      fillOpacity: highlight ? 0.5 : 0,
    });
  }

  getLiftColor(assetStatus: AssetStatus, element: MapElement, overrideColor = false): IconColor {
    if (element.pathColor != null && !overrideColor) {
      return { statusColor: element.pathColor, textColor: '#ffffff' };
    }

    switch (assetStatus) {
      case AssetStatus.Open:
        return { statusColor: '#00ff10', textColor: '#ffffff' };
      case AssetStatus.Closed:
        return { statusColor: '#df360e', textColor: '#ffffff' };
      case AssetStatus.Preparation:
        return { statusColor: '#e68523', textColor: '#ffffff' };
      case AssetStatus.Disabled:
        return { statusColor: '#555555', textColor: '#ffffff' };
      case AssetStatus.NoStatus:
        return { statusColor: '#555555', textColor: '#ffffff' };
      default:
        return { statusColor: '#555555', textColor: '#ffffff' };
    }
  }

  getColor(element: MapElement, useStatusColor?: boolean): IconColor {
    void useStatusColor;
    const status = element.status;

    switch (status) {
      case AssetStatus.Open:
        return { statusColor: '#00ff10', textColor: '#ffffff' };
      case AssetStatus.Closed:
        return { statusColor: '#df360e', textColor: '#ffffff' };
      case AssetStatus.Preparation:
        return { statusColor: '#e68523', textColor: '#ffffff' };
      case AssetStatus.Disabled:
        return { statusColor: '#ffffff', textColor: '#000000' };
      case AssetStatus.NoStatus:
        return { statusColor: '#ffffff', textColor: '#000000' };
      default:
        return { statusColor: '#ffffff', textColor: '#000000' };
    }
  }

  setIconColor(icon: Marker, backgroundColor: string, symbolColor?: string): void {
    void symbolColor;
    const e = icon.getElement()?.querySelectorAll('#circle')[0];
    e?.setAttribute('stroke', backgroundColor);
  }

  setSidepaneIconColor(iconSvg: string, backgroundColor: string, symbolColor?: string): string {
    void symbolColor;
    return this.setSvgColor('circle', backgroundColor, iconSvg);
  }

  private setSvgColor(id: string, color: string, iconSvg: string): string {
    const regex = new RegExp(`(id="${id}"[^>]*?)stroke="[^"]*"`);
    if (iconSvg.match(regex)) {
      iconSvg = iconSvg.replace(regex, `$1stroke="${color}"`);
    } else {
      iconSvg = iconSvg.replace(`id="${id}"`, `id="${id}" stroke="${color}"`);
    }
    return iconSvg;
  }

  setIconBorder(icon: Marker): void {
    void icon;
  }

  async createCurvePopupContent(
    element: MapElement,
    showLabel: boolean,
  ): Promise<{ popupContent: string; className: string }> {
    const label = showLabel && element.label ? `${element.label} ` : '';

    let svg: string;
    if (element.status != null && element.status !== AssetStatus.NoStatus) {
      const iconName = AssetStatus[element.status]?.toLocaleLowerCase();
      if (iconName) {
        svg = await this.getIconSvg(iconName);
      }
    }

    const icon = svg ? ` ${svg}` : '';
    const popupContent = `<span>${label}${element.name}</span>${icon}`;
    const className = `sis-map-tooltip-${this.name}`;

    return { popupContent, className };
  }

  async createPopupContent(
    element: MapElement,
    showLabel: boolean,
  ): Promise<{ popupContent: string; className: string }> {
    let svg: string;
    if (element.status != null && element.status !== AssetStatus.NoStatus && element.status !== AssetStatus.Disabled) {
      const iconName = AssetStatus[element.status]?.toLowerCase();

      if (iconName) {
        svg = await this.getIconSvg(iconName);
      }
    }

    const icon = svg ? ` ${svg}` : '';
    const label = showLabel && element.label ? `${element.label} ` : '';

    const popupContent = `<span>${label}${element.name}</span>${icon}`;
    const className = `sis-map-tooltip-${this.name}`;

    return { popupContent, className };
  }

  getSignPostSvgUrl(): string {
    return `${this.urlBlobPath}signpost.svg`;
  }
}
